import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ROLE_LABELS, ROLES, type Board, type Me, type Role, type UserSummary } from '@wo/shared';
import { api, errorText } from '../../lib/api';
import { useUsers } from '../../lib/hooks';
import { Button, ErrorText, Field, Input, Loading, Modal, PageHeader, Select, Table } from '../../components/ui';

export function UsersPage({ me }: { me: Me }) {
  const users = useUsers();
  const qc = useQueryClient();
  const [editing, setEditing] = useState<UserSummary | 'new' | null>(null);
  const [pinFor, setPinFor] = useState<UserSummary | null>(null);
  const board = useQuery({ queryKey: ['board'], queryFn: () => api.get<Board>('/api/board') });
  const lockedUntil = (id: number) => board.data?.mechanics.find((m) => m.user.id === id)?.lockedUntil ?? null;
  const isAdmin = me.role === 'ADMIN';
  if (users.isLoading) return <Loading />;
  return (
    <>
      <PageHeader title="Users">{isAdmin && <Button onClick={() => setEditing('new')}>Add user</Button>}</PageHeader>
      <p className="mb-3 text-sm text-slate-600">
        Office users sign in with their Google Workspace account. Mechanics sign in on an enrolled iPad with a PIN the Manager sets. Users are never deleted, only deactivated.
      </p>
      <div className="rounded-lg border border-slate-200 bg-white">
        <Table head={['Name', 'Role', 'Email', 'Status', '']}>
          {users.data?.map((u) => (
            <tr key={u.id} className={u.active ? '' : 'text-slate-400'}>
              <td className="px-3 py-2 font-medium">{u.name}</td>
              <td className="px-3 py-2">{ROLE_LABELS[u.role]}</td>
              <td className="px-3 py-2">{u.email ?? '—'}</td>
              <td className="px-3 py-2">
                {u.active ? 'Active' : 'Inactive'}
                {lockedUntil(u.id) && <span className="ml-2 rounded bg-amber-100 px-2 text-xs font-semibold text-amber-900">PIN locked</span>}
              </td>
              <td className="px-3 py-2 text-right">
                {u.role === 'MECHANIC' && u.active && <Button variant="ghost" onClick={() => setPinFor(u)}>Set PIN</Button>}
                {isAdmin && <Button variant="ghost" onClick={() => setEditing(u)}>Edit</Button>}
              </td>
            </tr>
          ))}
        </Table>
      </div>
      {pinFor && <PinDialog user={pinFor} onClose={() => setPinFor(null)} onSaved={() => { setPinFor(null); qc.invalidateQueries({ queryKey: ['board'] }); }} />}
      {editing && (
        <UserDialog
          me={me}
          user={editing === 'new' ? null : editing}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); qc.invalidateQueries({ queryKey: ['users'] }); qc.invalidateQueries({ queryKey: ['board'] }); }}
        />
      )}
    </>
  );
}

function UserDialog({ me, user, onClose, onSaved }: { me: Me; user: UserSummary | null; onClose: () => void; onSaved: () => void }) {
  const [f, setF] = useState({ name: user?.name ?? '', email: user?.email ?? '', role: (user?.role ?? 'MECHANIC') as Role, active: user?.active ?? true });
  const [error, setError] = useState<string | null>(null);
  const self = user?.id === me.id;
  const save = async () => {
    try {
      if (user) await api.patch(`/api/users/${user.id}`, f);
      else await api.post('/api/users', f);
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal title={user ? `Edit ${user.name}` : 'Add user'} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save}>Save</Button></>}>
      <ErrorText error={error} />
      <Field label="Name" required><Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} autoFocus /></Field>
      <Field label="Role" required>
        <Select value={f.role} onChange={(e) => setF({ ...f, role: e.target.value as Role })} disabled={self}>
          {ROLES.map((r) => <option key={r} value={r}>{ROLE_LABELS[r]}</option>)}
        </Select>
      </Field>
      <Field label="Google Workspace email" hint={f.role === 'MECHANIC' ? 'Optional for mechanics' : 'Required — used for Google sign-in'}>
        <Input type="email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} />
      </Field>
      {user && !self && (
        <label className="flex items-center gap-2 text-sm">
          <input type="checkbox" checked={f.active} onChange={(e) => setF({ ...f, active: e.target.checked })} /> Active (unticking signs them out everywhere)
        </label>
      )}
    </Modal>
  );
}

function PinDialog({ user, onClose, onSaved }: { user: UserSummary; onClose: () => void; onSaved: () => void }) {
  const [pin, setPin] = useState('');
  const [error, setError] = useState<string | null>(null);
  const save = async () => {
    try {
      await api.post(`/api/users/${user.id}/pin`, { pin });
      onSaved();
    } catch (e) {
      setError(errorText(e));
    }
  };
  return (
    <Modal title={`Set PIN for ${user.name}`} onClose={onClose} footer={<><Button variant="secondary" onClick={onClose}>Cancel</Button><Button onClick={save} disabled={pin.length !== 6}>Set PIN</Button></>}>
      <p className="text-sm text-slate-600">
        6 digits. Tell {user.name} in person. They choose their own PIN the first time they sign in. This also clears a lockout and signs them out everywhere.
      </p>
      <ErrorText error={error} />
      <Field label="Temporary PIN"><Input inputMode="numeric" maxLength={6} value={pin} onChange={(e) => setPin(e.target.value.replace(/\D/g, ''))} autoFocus /></Field>
    </Modal>
  );
}
